import { SALES_EMAIL, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Icon, Skeleton, buttonClasses } from "@sagot/ui";
import { usePermissions } from "../../../../session/permissions";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { offeredFeatureAddons } from "../../billing/billing-model";
import { useAddonCatalog, useBillingOverview } from "../../shared";

const K = "launch.upsell";
const POINTS = ["pointVouch", "pointCode", "pointTest"] as const;

/** The action of the card: only people who manage billing see a buy action. */
function Action() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const canBuy = usePermissions().can("billing.manage");
  const overview = useBillingOverview(canBuy);
  const catalog = useAddonCatalog(canBuy);
  if (!canBuy) return <p className="text-sm text-fg-muted" data-testid="upsell-ask">{t(`${K}.askOwner`)}</p>;
  if (overview.isPending || catalog.isPending) return <Skeleton className="h-9 w-48" />;
  const sold = overview.data && catalog.data ? offeredFeatureAddons(catalog.data, overview.data.plan.key, overview.data.features).includes("secure_launch") : false;
  if (sold) {
    return (
      <AppLink href={consoleRoutes.billing(summary.slug)} className={buttonClasses({ variant: "primary" })} data-testid="upsell-addon">
        <Icon name="plus" />
        {t(`${K}.addAddon`)}
      </AppLink>
    );
  }
  const subject = encodeURIComponent(t(`${K}.salesSubject`));
  return (
    <a href={`mailto:${SALES_EMAIL}?subject=${subject}`} className={buttonClasses({ variant: "primary" })} data-testid="upsell-sales">
      <Icon name="mail" />
      {t(`${K}.talkToSales`)}
    </a>
  );
}

/**
 * Settings > Secure launch without the entitlement: what the feature does and how to get it (Institution includes it; Standard
 * and Pro add it from Billing > Add-ons, or talk to sales when it is not on sale yet). No setting is editable here.
 */
export function LaunchUpsellCard() {
  const { t } = useTranslation();
  return (
    <section className="card card-body space-y-5" aria-labelledby="launch-upsell-title" data-testid="launch-upsell">
      <div className="flex items-start gap-4">
        <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-ink">
          <Icon name="shield-check" className="size-6" />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="launch-upsell-title" className="text-base font-bold">
              {t(`${K}.title`)}
            </h2>
            <Badge tone="warning">{t(`${K}.badge`)}</Badge>
          </div>
          <p className="mt-1 text-sm text-fg-muted">{t(`${K}.body`)}</p>
        </div>
      </div>
      <ul className="space-y-2 text-sm">
        {POINTS.map((point) => (
          <li key={point} className="flex items-start gap-2">
            <Icon name="check" className="mt-0.5 size-4 shrink-0 text-success" />
            <span>{t(`${K}.${point}`)}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-fg-muted" data-testid="upsell-plans">
        {t(`${K}.plans`)}
      </p>
      <Action />
    </section>
  );
}
